import React, {
  useMemo,
  Fragment,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useIntl } from "react-intl";
import { Row, Col, Alert, Divider } from "antd";

import AdrItem from "./adrItem";
import Service from "./service";
import AdrSummary from "./adrSummary";
import {
  loadUnClasses,
  loadPackagingTypes,
  loadPackagingGroups,
} from "../../../modules/staticDataLoad";
import {
  roundNumber,
  sumAdrQuantity,
  sumCollisWeight,
  sumAdrGrossWeight,
  sumAdrHandlingUnitsQuantity,
} from "../../../modules/consignmentFunctions";
import InfoLabel from "../../app/generic/infoLabel";
import IconButton from "../../app/generic/iconButton";
import LoadSkeleton from "../../app/generic/loadSkeleton";
import TextAreaItem from "../../app/generic/textAreaItem";

export const defaultAdr = [{ idx: 0 }],
  maxHandlingUnitsQuantity = 9999;

const Adr = ({
  rules,
  collis,
  mainForm,
  disabled,
  onSetAdrCount,
  initialServices,
  getFieldDecorator,
}) => {
  const formItemLayout = useMemo(
    () => ({
      colon: false,
      labelCol: {
        span: 7,
      },
      wrapperCol: {
        span: 8,
      },
    }),
    []
  );

  const hasAdr = useMemo(() => mainForm.getFieldValue("adr"), [mainForm]);

  const [maxCount, setMaxCount] = useState();
  const [maxWeight, setMaxWeight] = useState();
  useEffect(() => {
    setMaxCount(
      /*(collis && collis.length) ||*/ /*rules.count*/ maxHandlingUnitsQuantity
    );
    setMaxWeight((collis && sumCollisWeight(collis, true)) || rules.weight);
  }, [rules, collis]);

  const intl = useIntl();
  const [unClasses, setUnClasses] = useState();
  const [packagingTypes, setPackagingTypes] = useState();
  const [packagingGroups, setPackagingGroups] = useState();
  useEffect(() => {
    loadUnClasses(setUnClasses);
    loadPackagingTypes(setPackagingTypes);
    loadPackagingGroups(setPackagingGroups);
  }, [intl.locale]);

  const [quantity, setQuantity] = useState(0);
  const [grossWeight, setGrossWeight] = useState(0);
  const [handlingUnitsQuantity, setHandlingUnitsQuantity] = useState(0);
  const handleSetQuantity = useCallback(
    (value) => {
      setQuantity(roundNumber(value + quantity));
    },
    [quantity]
  );
  const handleSetGrossWeight = useCallback(
    (value) => {
      setGrossWeight(roundNumber(value + grossWeight));
    },
    [grossWeight]
  );
  const handleSetHandlingUnitsQuantity = useCallback(
    (value) => {
      setHandlingUnitsQuantity(value + handlingUnitsQuantity);
    },
    [handlingUnitsQuantity]
  );

  const [newIdx, setNewIdx] = useState(1);
  const [adrs, setAdrs] = useState(defaultAdr);
  const handleAdrDelete = useCallback(
    (idx, data) => {
      if (adrs.length > 0) {
        setAdrs([...adrs.filter((c) => c.idx !== idx)]);
        handleSetQuantity(data[0]);
        handleSetGrossWeight(data[1]);
        handleSetHandlingUnitsQuantity(data[2]);
      }
    },
    [
      adrs,
      handleSetQuantity,
      handleSetGrossWeight,
      handleSetHandlingUnitsQuantity,
    ]
  );
  const handleAdrAdd = useCallback(() => {
    setAdrs([...adrs, { idx: newIdx }]);
    setNewIdx(newIdx + 1);
  }, [newIdx, adrs]);

  useEffect(() => {
    adrs && onSetAdrCount && onSetAdrCount(hasAdr ? adrs.length : 0);
  }, [adrs, hasAdr, onSetAdrCount]);

  useEffect(() => {
    if (
      initialServices &&
      initialServices.adr &&
      initialServices.adr.length > 0
    ) {
      setNewIdx(initialServices.adr.length);
      setAdrs(initialServices.adr.map((a, idx) => ({ ...a, idx })));
      setQuantity(sumAdrQuantity(initialServices.adr));
      setGrossWeight(sumAdrGrossWeight(initialServices.adr));
      setHandlingUnitsQuantity(
        sumAdrHandlingUnitsQuantity(initialServices.adr)
      );
    } else {
      setAdrs([{ idx: 0 }]);
    }
  }, [initialServices]);

  return rules.adr ? (
    <>
      {hasAdr && <Divider />}
      {hasAdr && !disabled && (
        <Row gutter={[0, 20]}>
          <Col>
            <Alert
              closable
              type="warning"
              message={intl.formatMessage({
                id: "consignment.services.adr.info",
                defaultMessage:
                  "Pro možnosti přepravy ADR tříd nebezpečnosti 1.4; 4.2; 4.3; 5.2; je nutné vytvořit poptávku přes Zákaznické centrum na emailové adrese DHLFreight.cz@dhl.com",
              })}
            />
          </Col>
        </Row>
      )}
      <Row type="flex" align="middle">
        <Service
          hideRow
          label={intl.formatMessage({
            id: "consignment.services.adr",
            defaultMessage: "ADR",
          })}
          fieldName="adr"
          disabled={disabled}
          initialValue={
            (initialServices &&
              initialServices.adr &&
              initialServices.adr.length > 0) ||
            (disabled && initialServices && initialServices.adrInformation)
          }
          getFieldDecorator={getFieldDecorator}
        />
        <Col span={16}>
          {hasAdr ? (
            disabled && initialServices && initialServices.adrInformation ? (
              <TextAreaItem
                disabled
                allowClear
                wrapperCol={{ span: 9 }}
                fieldName="adrInformation"
                autoSize={{ minRows: 2, maxRows: 6 }}
                getFieldDecorator={getFieldDecorator}
                initialValue={initialServices.adrInformation}
                label={
                  <InfoLabel
                    info={intl.formatMessage({
                      id: "consignment.services.adrInfo",
                      defaultMessage:
                        "V případě ADR zásilky uveďte: UN číslo, oficiální pojmenování, třídu, obalovou skupinu a množství.",
                    })}
                  />
                }
              />
            ) : (
              <>
                <Row gutter={[0, 20]} className="margin-left-1">
                  <Col>
                    {adrs ? (
                      adrs.map((a, idx) => (
                        <Fragment key={a.idx}>
                          {idx > 0 && <Divider />}
                          <AdrItem
                            intl={intl}
                            index={a.idx}
                            rules={rules}
                            initialAdr={a}
                            count={adrs.length}
                            maxCount={maxCount}
                            disabled={disabled}
                            maxWeight={maxWeight}
                            unClasses={unClasses}
                            onAdrDelete={handleAdrDelete}
                            formItemLayout={formItemLayout}
                            packagingTypes={packagingTypes}
                            packagingGroups={packagingGroups}
                            onSetQuantity={handleSetQuantity}
                            getFieldDecorator={getFieldDecorator}
                            onSetGrossWeight={handleSetGrossWeight}
                            onSetHandlingUnitsQuantity={
                              handleSetHandlingUnitsQuantity
                            }
                          />
                        </Fragment>
                      ))
                    ) : (
                      <LoadSkeleton />
                    )}
                  </Col>
                </Row>
                <Row type="flex" justify="space-between" align="middle" gutter={[0, 20]}>
                  <Col>
                    <AdrSummary
                      quantity={quantity}
                      maxCount={maxCount}
                      maxWeight={maxWeight}
                      grossWeight={grossWeight}
                      handlingUnitsQuantity={handlingUnitsQuantity}
                    />
                  </Col>
                  <Col>
                    <IconButton
                      icon="plus"
                      shape="none"
                      onClick={handleAdrAdd}
                      disabled={!adrs || adrs.length === rules.count}
                      text={intl.formatMessage({
                        id: "consignment.services.adr.add",
                        defaultMessage: "Přidat ADR kus",
                      })}
                    />
                  </Col>
                </Row>
              </>
            )
          ) : (
            <Fragment />
          )}
        </Col>
      </Row>
      {hasAdr && <Divider />}
    </>
  ) : (
    <Fragment />
  );
};

export default Adr;
